// Карточка контрибьютора: кто это, как идут часы, что отклонили и почему,
// кто вёл человека раньше.
//
// Экран отвечает на один вопрос — «почему у него такие цифры»: сводка сверху,
// дневная динамика, список записей с причинами отказа. Отказы важнее принятых
// записей, поэтому они не прячутся за фильтр.
import { useEffect, useState } from 'preact/hooks';
import {
  api,
  type DatoricContributorRes,
  type DatoricPaymentRow,
  type DatoricStaffMe as Me,
} from '../api';
import { genPassword } from './ManagePage';

const fmtH = (h: number): string => h.toFixed(h >= 100 ? 0 : 1);
const fmtMoney = (cents: number | null): string =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;
const fmtDur = (sec: number | null): string => {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `0:${String(s).padStart(2, '0')}`;
};
const fmtDate = (ms: number | null): string =>
  ms ? new Date(ms).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) : '—';
const fmtDateTime = (ms: number | null): string =>
  ms
    ? new Date(ms).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

// Ярлыки статуса дублируют PoolPage намеренно: экран может открываться по
// прямой ссылке, и тянуть общий модуль ради двух строк дороже, чем держать их
// рядом с местом использования.
/** Дата и время заведения — админы просят абсолютную отметку, а не «сколько
 *  дней назад»: по относительной не сказать, когда именно человек появился. */
const stamp = (ms: number | null): string =>
  ms
    ? new Date(ms).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const STATUS_LABEL: Record<string, string> = {
  invited: 'приглашён',
  joined: 'вошёл',
  active: 'активен',
  blocked: 'заблокирован',
};

const REVIEW_LABEL: Record<string, string> = {
  approved: 'принято',
  pending: 'на проверке',
  rejected: 'отклонено',
};

export function ContributorPage({ me, id }: { me: Me; id: string }) {
  const [data, setData] = useState<DatoricContributorRes | null | 'error'>(null);
  const [tab, setTab] = useState<'all' | 'rejected'>('all');
  // Cabinet password reset (0004): the pool's admin issues new credentials,
  // shown once with a copy button.
  const [creds, setCreds] = useState<{ email: string; password: string } | null>(null);
  const [pwdBusy, setPwdBusy] = useState(false);
  const [payments, setPayments] = useState<DatoricPaymentRow[] | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('');
  const [payBusy, setPayBusy] = useState(false);
  const [payErr, setPayErr] = useState<string | null>(null);

  const resetPassword = async (email: string) => {
    setPwdBusy(true);
    try {
      const password = genPassword();
      await api.patchContributor(id, { password });
      setCreds({ email, password });
    } catch {
      setCreds(null);
    } finally {
      setPwdBusy(false);
    }
  };

  useEffect(() => {
    setData(null);
    api
      .contributor(id)
      .then(setData)
      .catch(() => setData('error'));
  }, [id]);

  // История выплат тянется отдельным запросом: карточка и без неё полезна, а
  // ждать обе ради одной таблицы незачем.
  const loadPayments = () => {
    setPayments(null);
    api
      .payments(id)
      .then((r) => setPayments(r.rows))
      .catch(() => setPayments([]));
  };
  useEffect(loadPayments, [id]);

  const addPayment = async () => {
    const dollars = Number(payAmount.replace(',', '.'));
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setPayErr('Сумма должна быть больше нуля.');
      return;
    }
    setPayBusy(true);
    setPayErr(null);
    try {
      await api.addPayment(id, Math.round(dollars * 100), payNote.trim() || undefined);
      setPayAmount('');
      setPayNote('');
      loadPayments();
      // Плитки «факт оплаты» и «задолженность» считает сервер — перечитываем
      // карточку, иначе цифры разойдутся с только что внесённой выплатой.
      api.contributor(id).then(setData).catch(() => undefined);
    } catch (e) {
      setPayErr(e instanceof Error ? e.message : 'Не удалось записать выплату.');
    } finally {
      setPayBusy(false);
    }
  };

  if (data === 'error') {
    return (
      <div class="empty">
        Не нашли этого человека — возможно, он в пуле другого админа.{' '}
        <a href="#/">Вернуться к пулу</a>
      </div>
    );
  }
  if (!data) return <div class="empty">Загружаем…</div>;

  const p = data.person;
  const rejected = data.recent.filter((r) => r.reviewStatus === 'rejected');
  const list = tab === 'rejected' ? rejected : data.recent;
  // Дневная кривая приходит с бэкенда свежими днями вперёд — для графика
  // разворачиваем, чтобы время шло слева направо.
  const days = [...data.daily].reverse();
  const maxDay = Math.max(1, ...days.map((d) => d.uploadedHours));

  return (
    <>
      <div class="crumbs">
        <a href="#/">← Мой пул</a>
      </div>

      <div class="card-head">
        <div>
          <h2>{p.name ?? p.email}</h2>
          <div class="muted">
            {p.name ? `${p.email} · ` : ''}
            {p.country ?? 'страна не указана'}
            {p.phone ? ` · ${p.phone}` : ''}
          </div>
          {/* Когда завели. Сам никто не регистрируется: админ добавляет почту и
              выдаёт пароль, так что это и есть дата регистрации. */}
          <div class="muted">в системе с {stamp(p.createdAt)}</div>
        </div>
        <div class="card-chips">
          <span class={`chip ${p.status}`}>{STATUS_LABEL[p.status] ?? p.status}</span>
          {(p.fraud || p.providerStatus === 'flagged' || p.providerStatus === 'disabled') && (
            <span class="chip fraud">{p.fraud ?? p.providerStatus}</span>
          )}
          {p.attribution !== 'matched' && <span class="chip joined">не сматчен</span>}
          {me.role !== 'viewer' && (
            <button type="button" class="ghost" disabled={pwdBusy} onClick={() => resetPassword(p.email)}>
              {pwdBusy ? 'Генерируем…' : 'Новый пароль кабинета'}
            </button>
          )}
        </div>
      </div>

      {creds && (
        <CredsBlock email={creds.email} password={creds.password} onClose={() => setCreds(null)} />
      )}

      {p.attribution !== 'matched' && (
        <div class="strip warn">
          Человек ещё не сматчен в сверке Datoric — часы не начнут считаться, пока не появится
          первая принятая запись.
        </div>
      )}

      <div class="tiles">
        <div class="tile"><div class="n">{fmtH(p.acceptedHours)} ч</div><div class="t">принято</div></div>
        <div class="tile"><div class="n">{fmtH(p.pendingHours)} ч</div><div class="t">на проверке</div></div>
        <div class="tile"><div class="n">{fmtH(p.rejectedHours)} ч</div><div class="t">брак</div></div>
        <div class="tile"><div class="n">{p.qualityScore ?? '—'}</div><div class="t">качество</div></div>
        <div class="tile">
          <div class="n">{p.earnedCents == null ? '—' : fmtMoney(p.earnedCents)}</div>
          <div class="t">{p.rateCents == null ? 'ставка не задана' : `заработал по $${(p.rateCents / 100).toFixed(2)}/ч`}</div>
        </div>
        <div class="tile">
          <div class="n">{p.paidCents > 0 ? fmtMoney(p.paidCents) : '—'}</div>
          <div class="t">факт оплаты</div>
        </div>
        <div class="tile">
          <div class={p.debtCents != null && p.debtCents > 0 ? 'n owed' : 'n'}>
            {p.debtCents == null ? '—' : fmtMoney(p.debtCents)}
          </div>
          <div class="t">задолженность</div>
        </div>
        {/* Provider revenue — the worker sends it to managers only. */}
        {p.payoutCents != null && (
          <div class="tile"><div class="n">{fmtMoney(p.payoutCents)}</div><div class="t">доход</div></div>
        )}
      </div>

      <div class="panel">
        <h3>Выплаты</h3>
        {/* Менеджер вносит ОДНУ выплату, накопленную сумму складывает сервер.
            Поле, в котором человек сам плюсует гонорар к прошлому, рано или
            поздно затрут вместо прибавления — и спорить придётся о цифре,
            происхождение которой никто не помнит. */}
        {me.role === 'manager' && (
          <div class="pay-form">
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="сумма, $"
              value={payAmount}
              disabled={payBusy}
              onInput={(e) => setPayAmount((e.target as HTMLInputElement).value)}
            />
            <input
              type="text"
              placeholder="комментарий (необязательно)"
              value={payNote}
              disabled={payBusy}
              maxLength={300}
              onInput={(e) => setPayNote((e.target as HTMLInputElement).value)}
            />
            <button type="button" disabled={payBusy} onClick={() => void addPayment()}>
              {payBusy ? 'Записываем…' : 'Записать выплату'}
            </button>
          </div>
        )}
        {payErr && <div class="strip warn">{payErr}</div>}
        {payments === null ? (
          <div class="muted">загружаем…</div>
        ) : payments.length === 0 ? (
          <div class="muted">выплат ещё не было</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Сколько</th>
                <th>Кто провёл</th>
                <th>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((x) => (
                <tr key={x.id}>
                  <td>{fmtDateTime(x.createdAt)}</td>
                  <td>{fmtMoney(x.amountCents)}</td>
                  <td>{x.byName ?? <span class="muted">—</span>}</td>
                  <td>{x.note ?? <span class="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {days.length > 0 && (
        <div class="panel">
          <h3>По дням</h3>
          {/* Столбики, а не библиотека графиков: нужен один взгляд «ровно
              работает или рывками», ради этого тянуть зависимость незачем. */}
          <div class="bars">
            {days.map((d) => (
              <div class="bar-col" key={d.day} title={`${d.day}: ${fmtH(d.uploadedHours)} ч`}>
                <div class="bar-stack" style={{ height: `${(d.uploadedHours / maxDay) * 100}%` }}>
                  <div class="bar-rej" style={{ flex: d.rejectedHours }} />
                  <div class="bar-pend" style={{ flex: d.pendingHours }} />
                  <div class="bar-acc" style={{ flex: d.acceptedHours }} />
                </div>
                <div class="bar-label">{d.day.slice(8)}</div>
              </div>
            ))}
          </div>
          <div class="legend">
            <span><i class="sw acc" /> принято</span>
            <span><i class="sw pend" /> на проверке</span>
            <span><i class="sw rej" /> брак</span>
          </div>
        </div>
      )}

      <div class="panel">
        <div class="panel-head">
          <h3>Записи</h3>
          <div class="tabs">
            <button class={tab === 'all' ? 'tab on' : 'tab'} onClick={() => setTab('all')}>
              Все ({data.recent.length})
            </button>
            <button
              class={tab === 'rejected' ? 'tab on' : 'tab'}
              onClick={() => setTab('rejected')}
            >
              Отклонённые ({rejected.length})
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <div class="empty">{tab === 'rejected' ? 'Отклонённых нет — хорошо.' : 'Записей пока нет.'}</div>
        ) : (
          <div class="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Файл</th>
                  <th>Снято</th>
                  <th>Длина</th>
                  <th>Статус</th>
                  <th>Качество</th>
                  <th>Разнообразие</th>
                  <th>Причина</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>{r.fileName ?? <span class="muted">без имени</span>}</td>
                    <td>{fmtDateTime(r.recordedAt ?? r.uploadedAt)}</td>
                    <td>{fmtDur(r.durationSeconds)}</td>
                    <td>
                      <span class={`chip ${r.reviewStatus === 'approved' ? 'active' : r.reviewStatus === 'rejected' ? 'fraud' : 'pend'}`}>
                        {REVIEW_LABEL[r.reviewStatus ?? ''] ?? r.reviewStatus ?? '—'}
                      </span>
                      {/* Сбой пайплайна — это не брак оператора, и путать их нельзя. */}
                      {r.pipelineStatus === 'failed' && <span class="chip fraud">сбой обработки</span>}
                    </td>
                    <td>{r.quality ?? '—'}</td>
                    <td>{r.diversity ?? '—'}</td>
                    <td class="reason">{r.rejectionReason ?? <span class="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.ownership.length > 0 && (
        <div class="panel">
          <h3>Кто вёл</h3>
          <ul class="owners">
            {data.ownership.map((o, i) => (
              <li key={i}>
                <b>{o.ownerName ?? '—'}</b>{' '}
                <span class="muted">
                  {fmtDate(o.fromAt)} — {o.toAt == null ? 'сейчас' : fmtDate(o.toAt)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// Local creds block (login + password one-liner): the ManagePage panel is about
// staff and carries its wording; this one is the contributor cabinet.
function CredsBlock({ email, password, onClose }: { email: string; password: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = `${email} ${password}`;
  return (
    <div class="creds">
      <pre>{text}</pre>
      <div class="creds-actions">
        <button
          type="button"
          class="primary"
          onClick={() => navigator.clipboard.writeText(text).then(() => setCopied(true))}
        >
          {copied ? '✓ Скопировано' : 'Скопировать логин-пароль'}
        </button>
        <button type="button" class="ghost" onClick={onClose}>Скрыть</button>
      </div>
      <div class="muted">Новый доступ в кабинет datoric.weern.com — показывается один раз.</div>
    </div>
  );
}
